import React, { useEffect, useRef, useState } from 'react'

import {
  Cell,
  EmptyState,
  ListLoading,
  Row,
  type SortColumnProps,
  StickyTable,
  StickyTableContainer,
} from '../../module'
import { debounce } from '../../services/HelperServiceTyped'
import { type EmptyStateProps } from '../EmptyState/EmptyState'
import SortColumn from '../SortColumn/SortColumn'
import UnsortedColumn from '../UnsortedColumn/UnsortedColumn'
import { useShowStickyClasses } from './helperFunctions'
import TableFilter from './TableFilter/TableFilter'
import TableGradient from './TableGradient'
import styles from './_table.module.scss'
import TableHeader from './TableHeader/TableHeader'

type TableFilterProps = React.ComponentProps<typeof TableFilter>
type TableHeaderProps = React.ComponentProps<typeof TableHeader>

type StickyTableConfigType = {
  /** The default for this is 1. If you need more than 1 sticky header, then use this prop. */
  header?: number
  /** The default for this is 1. If you need more than 1 sticky left column, then use this prop. */
  left?: number
  /** This should be set to 1 or more if the far right column has an action button in it. */
  right?: number
}

type HeaderItemType<StyleGeneric> = HeaderName & {
  label: SortColumnProps['label']
  lowerCaseParam?: SortColumnProps['lowerCaseParam']
  tooltip?: SortColumnProps['tooltip']
  className?: string
  noSort?: boolean
  filter?: SortColumnProps['filter']
  style?: StyleGeneric
}

type SingleNameHeader = {
  name: SortColumnProps['propName']
  options?: never
}

type OptionsHeader = {
  name?: never
  options: SortColumnProps['options']
}

type HeaderName = OptionsHeader | SingleNameHeader

type WithSorting = {
  noSort?: never
  /** Function to update the state of the sortBy prop */
  sort: SortColumnProps['sorter']
  /** Currently active sort column and direction */
  sortBy: SortColumnProps['active']
}

type WithoutSorting = {
  /** Remove sorting from the table */
  noSort: boolean
  sort?: never
  sortBy?: never
}

export type TableSortingProps = WithSorting | WithoutSorting

export type CustomTableProps<
  HeaderItem extends HeaderItemType<Record<string, unknown>>,
> = (
  | CustomTablePropsWithHeaders<HeaderItem>
  | CustomTablePropsWithCustomHeaders
) &
  TableSortingProps

type CustomTableBaseProps = {
  /** Flag to determine if the table has data available */
  hasData: boolean
  /** Flag to determine if the table has more data that needs to be called */
  hasMore: boolean
  /** Flag to let the table know that the api call was successful */
  successStatus: boolean
  /** Flag to determine if the data is being fetched */
  loading: boolean
  /** Each table needs a unique id to be able to handle setting the sort values in localStorage correctly */
  tableId: string
  /** This is the text that is displayed when no data is available */
  noDataFields: EmptyStateProps
  /** With pagination, new data needs to get called every time we reach the scrolling threshold. This is the function to get the next set of data. */
  getData: () => void
  children: React.ReactNode
  /** We need to know if there is a custom child (such as a checkbox) so that we can correctly determine which column headers should have the sticky classes. This is necessary to have the correct styles in the header. */
  headerChildren?: { jsx: React.ReactNode; count: number }
  /** Array of selected columns that we want displayed in the UI */
  customColumnsList?: Record<string, React.ReactNode[]> | React.ReactNode[]
  /** The table needs to know which columns and rows are sticky. This object is where we define that. */
  stickyTableConfig?: StickyTableConfigType
  /** You may define a set height for the table - optional */
  customHeight?: number | string
  /** You may define a set width for the table - optional */
  customWidth?: number | string
  /** If the table is next to another element, the computed width of the table will be incorrect. Use this to input the width of the element (including padding, margin, and other spacing) to have the correct width for the table. */
  widthOffset?: number
  /** Table filters that are currently in use. A table does not have to have filters, but if they do have filters, we need to know which are the active filters. */
  /** @deprecated: Use tableHeaderProps instead */
  activeFilters?: TableFilterProps['activeFilters']
  /** Function to remove the currently active filters. */
  /** @deprecated: Use tableHeaderProps instead */
  removeFilters?: TableFilterProps['remove']
  /** Shows a short list loading experience */
  shortListLoading?: boolean
  /** Flag to hide gradient when data is present */
  noGradient?: boolean
  /** Table header configuration - this includes information about a table: header text and value, searching, CSV download, filtering, informational tooltips, and other configurations for a table. */
  tableHeaderProps?: TableHeaderProps
  /** Optional prop to add a test id to the CustomTable for QA testing */
  qaTestId?: string
}

type CustomTablePropsWithHeaders<HeaderItem> = CustomTableBaseProps & {
  /** Array of table headers */
  headers: HeaderItem[]
  customHeaders?: never
}

type CustomTablePropsWithCustomHeaders = CustomTableBaseProps & {
  headers?: never
  /** Custom table headers. This would require building a header row with the `Row` and `Cell` components. */
  customHeaders: React.ReactNode
}

const CustomTable = <
  HeaderItem extends HeaderItemType<Record<string, unknown>>,
>({
  hasData,
  hasMore,
  successStatus,
  loading,
  tableId,
  noDataFields,
  sort,
  sortBy,
  noSort,
  getData,
  children,
  headers,
  customHeaders,
  headerChildren,
  stickyTableConfig,
  customHeight,
  customWidth,
  widthOffset,
  activeFilters,
  removeFilters,
  shortListLoading,
  noGradient,
  tableHeaderProps,
  qaTestId = 'custom-table',
}: CustomTableProps<HeaderItem>): React.JSX.Element => {
  const [state, setState] = useState({
      showStickyClasses: false,
    }),
    { showStickyClasses } = state
  const infiniteLoadRef = useRef<HTMLDivElement>(null),
    tableRef = useRef<HTMLDivElement>(null),
    tableWidth = tableRef.current?.clientWidth,
    innerTableWidth = tableRef.current?.querySelector(
      '.sticky-table-table',
    )?.clientWidth

  const isShowStickyClasses = useShowStickyClasses({
    innerTableWidth: innerTableWidth ?? 0,
    tableContainerWidth: tableWidth ?? 0,
  })

  useEffect(() => {
    // Handler to call on window resize
    const handleResize = debounce(() => {
      setState((prevState) => ({
        ...prevState,
        showStickyClasses: isShowStickyClasses,
      }))
    }, 250)
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [
    tableWidth,
    innerTableWidth,
    hasData,
    isShowStickyClasses,
    // Sticky classes might need to be re-evaluated if the header count changes
    headers?.length,
  ])

  useEffect(() => {
    if (infiniteLoadRef.current && hasMore && successStatus) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              getData()
            }
          })
        },
        { threshold: 0.5 },
      )

      observer.observe(infiniteLoadRef.current)

      return () => {
        observer.disconnect()
      }
    }
  }, [hasMore, successStatus, getData])

  return (
    <div
      className={showStickyClasses ? styles.stickyClasses : ''}
      qa-test-id={qaTestId}
    >
      <StickyTableContainer
        hasData={hasData}
        loading={loading}
        customHeight={customHeight}
        customWidth={customWidth}
        widthOffset={widthOffset}
      >
        {tableHeaderProps ? (
          <div className={styles.tableHeaderContainer}>
            <TableHeader {...tableHeaderProps} />
          </div>
        ) : null}
        {activeFilters && removeFilters && (
          <TableFilter activeFilters={activeFilters} remove={removeFilters} />
        )}
        <StickyTable
          stickyHeaderCount={stickyTableConfig?.header ?? 1}
          leftStickyColumnCount={stickyTableConfig?.left ?? 1}
          rightStickyColumnCount={stickyTableConfig?.right ?? 0}
          borderWidth={0}
          wrapperRef={tableRef}
          className={hasData ? styles.showScrollbar : ''}
        >
          <Row className={styles.stickyTableHeader}>
            {headerChildren?.jsx}
            {customHeaders
              ? customHeaders
              : headers?.map((th: HeaderItem, i: number) => {
                  const lastLeftStickyColumn =
                      // If headerChildren exist, then we need to subtract the number of headerChildren and then we naturally subtract 1 to account for the 0 index in arrays.
                      stickyTableConfig?.left
                        ? stickyTableConfig?.left -
                          (headerChildren?.count ?? 0) -
                          1
                        : 0,
                    firstRightStickyColumn =
                      headers &&
                      !!stickyTableConfig?.right &&
                      headers.length - stickyTableConfig?.right

                  return (
                    <Cell
                      key={th.name}
                      style={{ ...th.style }}
                      className={`${
                        lastLeftStickyColumn === i
                          ? styles.lastStickyCellLeft
                          : ''
                      } ${
                        Number(lastLeftStickyColumn) + 1 === i
                          ? styles.extraLeftPadding
                          : ''
                      } ${
                        firstRightStickyColumn === i
                          ? `${styles.firstStickyCellRight} ${styles.extraLeftPadding}`
                          : ''
                      } ${th.className ?? ''}`.trim()}
                    >
                      {/* Show sortable column only when both table and column sorting are enabled with data available */}
                      {!noSort && sort && sortBy && !th.noSort && hasData ? (
                        <SortColumn
                          label={th.label}
                          {...(th.options
                            ? { options: th.options }
                            : { propName: th.name ?? '' })}
                          sorter={sort}
                          customClass={th.className}
                          active={sortBy}
                          localStorageName={`sort_by_${tableId}`}
                          lowerCaseParam={th.lowerCaseParam}
                          tooltip={th.tooltip}
                          filter={th.filter}
                          activeFilters={activeFilters}
                          noSort={th.noSort}
                        />
                      ) : (
                        <UnsortedColumn label={th.label} tooltip={th.tooltip} />
                      )}
                    </Cell>
                  )
                })}
          </Row>
          {!loading ? (
            hasData && (
              <>
                {children && children}
                {hasMore && (
                  <div
                    ref={infiniteLoadRef}
                    className={styles.listLoadingWrapper}
                  >
                    <ListLoading />
                  </div>
                )}
              </>
            )
          ) : (
            <div className={styles.listLoadingWrapper}>
              <ListLoading longList={!shortListLoading} />
            </div>
          )}
        </StickyTable>
        {/* show gradient for more records */}
        {hasData && !noGradient && (
          <TableGradient showStickyClasses={showStickyClasses} />
        )}
      </StickyTableContainer>
      {!loading && !hasData && <EmptyState {...noDataFields} />}
    </div>
  )
}

export default CustomTable
